import { useEffect, useRef, useState, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { MessageSquare, ChevronRight, ChevronLeft, CalendarDays, MapPin, Send, Paperclip, Smile, Trash2, Pin, PinOff, Megaphone, FileText, Download, Loader2, Crown, ShieldAlert, X, FileSpreadsheet, FileImage, File as FileIcon } from "lucide-react";
import { api, apiError, formatDate } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";

const REACTIONS = ["❤️", "👍", "🔥", "🎉", "😂", "👏"];
const EMOJIS = ["😀", "😁", "😂", "🤣", "😅", "😊", "😍", "😎", "🤩", "🥳", "🤔", "😇", "😴", "😭", "😡", "👍", "👎", "👏", "🙏", "💪", "🔥", "🎉", "🎸", "🎹", "🥁", "🎤", "🎵", "❤️", "💯", "✅", "🚀", "⏰", "📍", "🎯", "☕", "🍺"];
const PLACEHOLDERS = [
  "https://images.unsplash.com/photo-1493225457124-a3eb161ffa5f?crop=entropy&cs=srgb&fm=jpg&q=80&w=400",
  "https://images.unsplash.com/photo-1501386761578-eac5c94b800a?crop=entropy&cs=srgb&fm=jpg&q=80&w=400",
  "https://images.unsplash.com/photo-1470229722913-7c0e2dbbafd3?crop=entropy&cs=srgb&fm=jpg&q=80&w=400",
];
const coverFor = (e) => e.image || PLACEHOLDERS[((e.event_id || e.id || "a").charCodeAt(0)) % PLACEHOLDERS.length] || PLACEHOLDERS[0];
const fileUrl = (fid) => `${process.env.REACT_APP_BACKEND_URL}/api/files/${fid}`;
const timeOf = (iso) => { try { return new Date(iso).toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" }); } catch { return ""; } };
const dayOf = (iso) => { try { return new Date(iso).toLocaleDateString("pt-BR", { weekday: "short", day: "2-digit", month: "short" }); } catch { return ""; } };
const kb = (n) => (n >= 1048576 ? `${(n / 1048576).toFixed(1)} MB` : `${Math.max(1, Math.round(n / 1024))} KB`);
const Avatar = ({ src, name, size = "h-10 w-10" }) => (
  <div className={`${size} rounded-full bg-[#1C1C24] border border-white/10 overflow-hidden flex items-center justify-center text-sm font-bold text-accent shrink-0`}>
    {src ? <img src={src} alt="" className="h-full w-full object-cover" /> : (name?.[0]?.toUpperCase() || "?")}
  </div>
);
const FileTypeIcon = ({ kind, ctype }) => {
  if (kind === "image" || (ctype || "").startsWith("image/")) return <FileImage className="h-5 w-5 text-sky-400" />;
  if (kind === "pdf") return <FileText className="h-5 w-5 text-rose-400" />;
  if ((ctype || "").includes("sheet") || (ctype || "").includes("excel")) return <FileSpreadsheet className="h-5 w-5 text-emerald-400" />;
  return <FileIcon className="h-5 w-5 text-zinc-400" />;
};

function ChatList() {
  const navigate = useNavigate();
  const [rows, setRows] = useState(null);
  useEffect(() => { api.get("/my/chats").then((r) => setRows(r.data)).catch(() => setRows([])); }, []);
  return (
    <div className="max-w-2xl mx-auto space-y-5" data-testid="messages-list-page">
      <div>
        <h1 className="font-head text-4xl sm:text-5xl font-extrabold tracking-tight leading-[1.05]">Mensagens</h1>
        <p className="text-zinc-400 mt-2 text-sm">Converse com sua equipe e mantenha tudo alinhado para o evento.</p>
      </div>
      {rows === null ? (
        <div className="space-y-3">{[0, 1, 2].map((i) => <div key={i} className="h-24 rounded-2xl bg-card border border-white/5 animate-pulse" />)}</div>
      ) : rows.length === 0 ? (
        <div className="text-center py-16" data-testid="messages-empty">
          <div className="h-14 w-14 rounded-2xl bg-white/5 flex items-center justify-center mx-auto mb-3"><MessageSquare className="h-7 w-7 text-zinc-600" /></div>
          <p className="text-sm text-zinc-500">Você ainda não participa de nenhum chat de evento.<br />Confirme-se em um evento ou crie o seu para começar a conversar.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {rows.map((c) => (
            <button key={c.event_id} data-testid={`chat-card-${c.event_id}`} onClick={() => navigate(`/mensagens/${c.event_id}`)}
              className="w-full text-left flex gap-3 items-center rounded-2xl border border-white/[0.07] bg-card p-3 hover:border-accent/40 transition-colors">
              <div className="relative w-16 h-16 rounded-xl overflow-hidden shrink-0 bg-[#1a1a20]" style={{ backgroundImage: `linear-gradient(to top,rgba(9,9,11,.55),rgba(9,9,11,.05)),url(${coverFor(c)})`, backgroundSize: "cover", backgroundPosition: "center" }}>
                {c.type && <span className="absolute bottom-1 left-1 text-[9px] font-bold px-1.5 py-0.5 rounded bg-[#FF4D00] text-white">{c.type}</span>}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between gap-2">
                  <h3 className="font-head font-bold text-base truncate">{c.name}</h3>
                  {c.last && <span className="text-[10px] text-zinc-500 shrink-0">{timeOf(c.last.created_at)}</span>}
                </div>
                <p className="text-xs text-zinc-400 truncate mt-0.5">{c.last ? c.last.preview : (c.available ? "Diga olá para a equipe!" : "Chat abre quando houver músico confirmado")}</p>
                <div className="flex items-center justify-between mt-1.5">
                  <div className="flex -space-x-2">
                    {(c.members || []).slice(0, 4).map((m, i) => <Avatar key={i} src={m.photo} name={m.stage_name} size="h-6 w-6 text-[10px]" />)}
                  </div>
                  {c.unread > 0 && <span data-testid={`chat-unread-${c.event_id}`} className="min-w-[20px] h-5 px-1.5 rounded-full bg-[#FF4D00] text-white text-[10px] font-bold flex items-center justify-center">{c.unread > 9 ? "9+" : c.unread}</span>}
                </div>
              </div>
              <ChevronRight className="h-5 w-5 text-zinc-600 shrink-0" />
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function ReactionBar({ m, reactRow, setReactRow, onReact, mine }) {
  const open = reactRow === m.id;
  const entries = Object.entries(m.reactions || {});
  return (
    <div className={`flex items-center gap-1.5 mt-1 flex-wrap ${mine ? "justify-end" : ""}`}>
      {entries.map(([emo, users]) => (
        <button key={emo} data-testid={`reaction-${m.id}-${emo}`} onClick={() => onReact(m.id, emo)}
          className="flex items-center gap-1 text-xs bg-[#1C1C24] border border-white/10 rounded-full px-2 py-0.5 hover:border-accent/40">
          <span>{emo}</span><span className="text-zinc-300 font-semibold">{users.length}</span>
        </button>
      ))}
      <div className="relative">
        <button data-testid={`react-btn-${m.id}`} onClick={() => setReactRow(open ? null : m.id)} className="text-zinc-500 hover:text-accent p-0.5"><Smile className="h-3.5 w-3.5" /></button>
        {open && (
          <div className="absolute z-20 bottom-6 left-0 flex gap-1 bg-[#121217] border border-white/10 rounded-full px-2 py-1 shadow-xl" data-testid={`react-picker-${m.id}`}>
            {REACTIONS.map((e) => <button key={e} onClick={() => { onReact(m.id, e); setReactRow(null); }} className="hover:scale-125 transition-transform text-base">{e}</button>)}
          </div>
        )}
      </div>
    </div>
  );
}

function MessageRow({ m, mine, isOrganizer, onReact, onPin, onDelete, reactRow, setReactRow }) {
  if (m.message_type === "SYSTEM") return (
    <div className="text-center my-2"><span className="text-[11px] text-zinc-500 bg-[#09090B] border border-white/[0.06] rounded-full px-3 py-1">{m.content}</span></div>
  );
  if (m.message_type === "ANNOUNCEMENT") return (
    <div className="my-2 rounded-2xl border border-accent/50 bg-accent/[0.07] p-3" data-testid={`msg-${m.id}`}>
      <div className="flex items-center gap-2 flex-wrap">
        <Crown className="h-4 w-4 text-accent" />
        <span className="font-head font-bold text-accent text-sm">{m.sender_name || "Organizador"}</span>
        <span className="flex items-center gap-1 text-[10px] font-bold px-2 py-0.5 rounded-full bg-[#FF4D00] text-white"><Megaphone className="h-3 w-3" />Aviso</span>
        <span className="text-[10px] text-zinc-500 ml-auto">{timeOf(m.created_at)}</span>
      </div>
      <p className="text-sm text-zinc-100 mt-1.5 whitespace-pre-wrap break-words">{m.content}</p>
      <ReactionBar m={m} reactRow={reactRow} setReactRow={setReactRow} onReact={onReact} mine={false} />
    </div>
  );
  return (
    <div className={`flex gap-2.5 my-3 group ${mine ? "flex-row-reverse" : ""}`} data-testid={`msg-${m.id}`}>
      {!mine && <Avatar src={m.sender_photo} name={m.sender_name} />}
      <div className={`max-w-[80%] min-w-0 ${mine ? "items-end flex flex-col" : ""}`}>
        {!mine && (
          <div className="flex items-center gap-2 mb-1">
            <span className="font-head font-bold text-sm text-zinc-100">{m.sender_name}</span>
            {m.sender_role && <span className="text-[11px] text-accent">{m.sender_role}</span>}
            <span className="text-[10px] text-zinc-500">{timeOf(m.created_at)}</span>
            {m.pinned && <Pin className="h-3 w-3 text-accent" />}
          </div>
        )}
        <div className={`rounded-2xl px-3.5 py-2.5 ${mine ? "bg-[#FF4D00]/15 border border-[#FF4D00]/30" : "bg-[#1C1C24] border border-white/[0.06]"}`}>
          {m.content && <p className={`text-sm break-words whitespace-pre-wrap ${m.deleted ? "italic text-zinc-500" : "text-zinc-100"}`}>{m.content}</p>}
          {m.file && (
            <a href={fileUrl(m.file.file_id)} target="_blank" rel="noreferrer" data-testid={`file-${m.id}`}
              className={`flex items-center gap-3 mt-2 rounded-xl border border-white/10 bg-black/30 p-2.5 hover:border-accent/40 transition-colors ${m.file.kind === "image" ? "flex-col items-stretch" : ""}`}>
              {m.file.kind === "image" ? (
                <img src={fileUrl(m.file.file_id)} alt={m.file.file_name} className="rounded-lg max-h-56 w-full object-cover" />
              ) : (
                <>
                  <span className="h-9 w-9 rounded-lg bg-[#1C1C24] flex items-center justify-center shrink-0"><FileTypeIcon kind={m.file.kind} ctype={m.file.file_type} /></span>
                  <span className="min-w-0 flex-1">
                    <span className="block text-sm text-zinc-100 truncate">{m.file.file_name}</span>
                    <span className="block text-[11px] text-zinc-500">{kb(m.file.file_size || 0)}</span>
                  </span>
                  <Download className="h-4 w-4 text-zinc-400 shrink-0" />
                </>
              )}
            </a>
          )}
        </div>
        {mine && <span className="text-[10px] text-zinc-500 mt-1">{timeOf(m.created_at)}</span>}
        {!m.deleted && <ReactionBar m={m} reactRow={reactRow} setReactRow={setReactRow} onReact={onReact} mine={mine} />}
      </div>
      {!m.deleted && (
        <div className="flex flex-col gap-1 opacity-0 group-hover:opacity-100 transition-opacity self-center">
          {isOrganizer && <button data-testid={`pin-${m.id}`} onClick={() => onPin(m)} title={m.pinned ? "Desafixar" : "Fixar"} className="text-zinc-500 hover:text-accent">{m.pinned ? <PinOff className="h-3.5 w-3.5" /> : <Pin className="h-3.5 w-3.5" />}</button>}
          {mine && <button data-testid={`del-${m.id}`} onClick={() => onDelete(m.id)} title="Excluir" className="text-zinc-500 hover:text-red-400"><Trash2 className="h-3.5 w-3.5" /></button>}
        </div>
      )}
    </div>
  );
}

function Conversation({ eventId }) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [meta, setMeta] = useState(null);
  const [denied, setDenied] = useState(false);
  const [tab, setTab] = useState("chat");
  const [msgs, setMsgs] = useState([]);
  const [files, setFiles] = useState([]);
  const [announcements, setAnnouncements] = useState([]);
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [emojiOpen, setEmojiOpen] = useState(false);
  const [reactRow, setReactRow] = useState(null);
  const [announceText, setAnnounceText] = useState("");
  const boxRef = useRef(null);
  const fileRef = useRef(null);
  const atBottom = useRef(true);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/events/${eventId}/chat`);
      setMeta(data);
      if (data.available) {
        const m = await api.get(`/events/${eventId}/chat/messages?limit=50`);
        setMsgs(m.data.messages);
        if (data.unread > 0) api.post(`/events/${eventId}/chat/read`).catch(() => {});
      }
    } catch (e) { if (e.response?.status === 403) setDenied(true); }
  }, [eventId]);

  const loadTab = useCallback(async (t) => {
    if (t === "arquivos") { try { const { data } = await api.get(`/events/${eventId}/chat/files`); setFiles(data.files); } catch { /* noop */ } }
    if (t === "avisos") { try { const { data } = await api.get(`/events/${eventId}/chat/announcements`); setAnnouncements(data.announcements); } catch { /* noop */ } }
  }, [eventId]);

  useEffect(() => { load(); const t = setInterval(() => { if (!document.hidden && tab === "chat") load(); }, 4000); return () => clearInterval(t); }, [load, tab]);
  useEffect(() => { if (reactRow) setEmojiOpen(false); }, [reactRow]);
  useEffect(() => { loadTab(tab); }, [tab, loadTab]);
  useEffect(() => { if (atBottom.current && boxRef.current) boxRef.current.scrollTop = boxRef.current.scrollHeight; }, [msgs]);
  const onScroll = () => { const el = boxRef.current; if (el) atBottom.current = el.scrollHeight - el.scrollTop - el.clientHeight < 80; };

  const send = async () => {
    const v = text.trim(); if (!v) return;
    setSending(true); atBottom.current = true;
    try { await api.post(`/events/${eventId}/chat/messages`, { content: v }); setText(""); setEmojiOpen(false); await load(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setSending(false); }
  };
  const onReact = async (mid, emoji) => {
    try { const { data } = await api.post(`/events/${eventId}/chat/messages/${mid}/react`, { emoji }); setMsgs((p) => p.map((x) => x.id === mid ? { ...x, reactions: data.reactions } : x)); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const onPin = async (m) => {
    try { m.pinned ? await api.delete(`/events/${eventId}/chat/messages/${m.id}/pin`) : await api.post(`/events/${eventId}/chat/messages/${m.id}/pin`); await load(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const onDelete = async (mid) => { try { await api.delete(`/events/${eventId}/chat/messages/${mid}`); await load(); } catch (e) { toast.error(apiError(e.response?.data?.detail)); } };
  const onFile = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    if (file.size > 10 * 1024 * 1024) { toast.error("Arquivo muito grande (máx. 10MB)."); e.target.value = ""; return; }
    setUploading(true); atBottom.current = true;
    const fd = new FormData(); fd.append("file", file);
    try { await api.post(`/events/${eventId}/chat/files`, fd); await load(); if (tab === "arquivos") loadTab("arquivos"); }
    catch (err) { toast.error(apiError(err.response?.data?.detail)); }
    finally { setUploading(false); e.target.value = ""; }
  };
  const postAnnounce = async () => {
    const v = announceText.trim(); if (!v) return;
    try { await api.post(`/events/${eventId}/chat/announce`, { content: v }); setAnnounceText(""); loadTab("avisos"); toast.success("Aviso publicado."); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  if (denied) return <div className="max-w-2xl mx-auto text-center py-20" data-testid="chat-denied"><ShieldAlert className="h-10 w-10 text-zinc-600 mx-auto mb-3" /><p className="text-zinc-400">Você não tem acesso ao chat deste evento.</p><Button onClick={() => navigate("/mensagens")} variant="outline" className="mt-4 rounded-xl bg-transparent border-white/10">Voltar</Button></div>;
  if (!meta) return <div className="max-w-2xl mx-auto py-20 flex justify-center"><Loader2 className="h-6 w-6 text-accent animate-spin" /></div>;

  const ev = meta.event || {};
  const isOrg = meta.is_organizer;
  const TABS = [{ id: "chat", label: "Chat", icon: MessageSquare }, { id: "arquivos", label: "Arquivos", icon: Paperclip }, { id: "avisos", label: "Avisos", icon: Megaphone }];

  return (
    <div className="max-w-2xl mx-auto flex flex-col gap-4" data-testid="conversation-page">
      {/* Event selector card */}
      <button data-testid="event-selector" onClick={() => navigate("/mensagens")} className="w-full text-left flex gap-3 items-center rounded-2xl border border-white/[0.07] bg-card p-3 hover:border-accent/40 transition-colors">
        <ChevronLeft className="h-5 w-5 text-zinc-500 shrink-0" />
        <div className="w-14 h-14 rounded-xl overflow-hidden shrink-0 bg-[#1a1a20]" style={{ backgroundImage: `linear-gradient(to top,rgba(9,9,11,.5),rgba(9,9,11,.05)),url(${coverFor(ev)})`, backgroundSize: "cover", backgroundPosition: "center" }} />
        <div className="flex-1 min-w-0">
          <h2 className="font-head font-bold text-lg truncate">{ev.name}</h2>
          <div className="flex items-center gap-1.5 text-xs text-zinc-400 mt-0.5"><CalendarDays className="h-3.5 w-3.5 text-accent shrink-0" /><span className="truncate">{formatDate(ev.date)} • {ev.start_time}</span></div>
          <div className="flex items-center gap-1.5 text-xs text-zinc-400 mt-0.5 min-w-0"><MapPin className="h-3.5 w-3.5 text-accent shrink-0" /><span className="truncate">{ev.address || "Local a definir"}</span></div>
        </div>
        <div className="flex -space-x-2 shrink-0">{(meta.members || []).slice(0, 4).map((m) => <Avatar key={m.user_id} src={m.photo} name={m.stage_name} size="h-7 w-7 text-[10px]" />)}</div>
      </button>

      {/* Tabs */}
      <div className="grid grid-cols-3 gap-2" data-testid="chat-tabs">
        {TABS.map((t) => (
          <button key={t.id} data-testid={`chattab-${t.id}`} onClick={() => setTab(t.id)}
            className={`flex items-center justify-center gap-2 h-11 rounded-xl text-sm font-semibold border transition-colors ${tab === t.id ? "bg-gradient-to-r from-[#FF4D00] to-accent text-white border-transparent shadow-[0_0_14px_rgba(255,79,0,0.25)]" : "bg-card border-white/10 text-zinc-400 hover:text-zinc-200"}`}>
            <t.icon className="h-4 w-4" />{t.label}
          </button>
        ))}
      </div>

      {!meta.available ? (
        <div className="rounded-2xl border border-white/[0.07] bg-card p-6 text-center text-sm text-zinc-500" data-testid="chat-unavailable">O chat será liberado quando houver ao menos um músico confirmado no evento.</div>
      ) : tab === "chat" ? (
        <div className="rounded-2xl border border-white/[0.07] bg-card overflow-hidden flex flex-col">
          {meta.pinned && (
            <div className="flex items-start gap-3 border-b border-accent/20 bg-accent/[0.06] p-3" data-testid="pinned-banner">
              <Pin className="h-4 w-4 text-accent mt-0.5 shrink-0" />
              <div className="min-w-0 flex-1">
                <div className="flex items-center justify-between gap-2"><span className="text-xs font-semibold text-accent">Fixado{meta.pinned.sender_name ? ` • ${meta.pinned.sender_name}` : ""}</span><span className="text-[10px] text-zinc-500">{dayOf(meta.pinned.created_at)}</span></div>
                <p className="text-sm text-zinc-100 truncate">{meta.pinned.content || (meta.pinned.file ? `📎 ${meta.pinned.file.file_name}` : "")}</p>
              </div>
              {isOrg && <button data-testid="unpin-btn" onClick={() => onPin(meta.pinned)} className="text-zinc-500 hover:text-red-400 shrink-0"><X className="h-4 w-4" /></button>}
            </div>
          )}
          <div ref={boxRef} onScroll={onScroll} className="overflow-y-auto px-4 py-2 max-h-[calc(100vh-420px)] min-h-[300px]" data-testid="chat-messages">
            {msgs.length === 0 && <p className="text-center text-sm text-zinc-600 py-10">Nenhuma mensagem ainda. Diga olá para a equipe!</p>}
            {msgs.map((m) => <MessageRow key={m.id} m={m} mine={m.user_id === user?.id} isOrganizer={isOrg} onReact={onReact} onPin={onPin} onDelete={onDelete} reactRow={reactRow} setReactRow={setReactRow} />)}
          </div>
          {meta.can_send ? (
            <div className="relative border-t border-white/[0.06] p-2.5">
              {emojiOpen && (
                <div className="absolute bottom-16 left-2 right-2 z-20 bg-[#121217] border border-white/10 rounded-2xl p-2 grid grid-cols-9 gap-1 shadow-xl" data-testid="emoji-picker">
                  {EMOJIS.map((e) => <button key={e} onClick={() => { setText((t) => t + e); }} className="text-xl hover:scale-125 transition-transform p-0.5">{e}</button>)}
                </div>
              )}
              <div className="flex items-center gap-2">
                <input ref={fileRef} type="file" className="hidden" onChange={onFile} data-testid="chat-file-input" />
                <button data-testid="chat-attach" onClick={() => fileRef.current?.click()} disabled={uploading} className="h-10 w-10 rounded-full border border-white/10 flex items-center justify-center text-zinc-400 hover:text-accent hover:border-accent/40 shrink-0">{uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Paperclip className="h-4 w-4" />}</button>
                <div className="relative flex-1">
                  <Input data-testid="chat-input" value={text} onChange={(e) => setText(e.target.value)} onKeyDown={(e) => e.key === "Enter" && !e.shiftKey && (e.preventDefault(), send())} placeholder="Digite uma mensagem..." className="bg-[#09090B] border-white/10 rounded-full pr-10 h-10" />
                  <button data-testid="chat-emoji" onClick={() => { setEmojiOpen((v) => !v); setReactRow(null); }} className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-400 hover:text-accent"><Smile className="h-5 w-5" /></button>
                </div>
                <Button data-testid="chat-send" onClick={send} disabled={sending || !text.trim()} className="h-10 w-10 p-0 rounded-full bg-gradient-to-r from-[#FF4D00] to-accent shrink-0"><Send className="h-4 w-4" /></Button>
              </div>
            </div>
          ) : <div className="p-3 border-t border-white/[0.06] text-center text-xs text-zinc-500">Chat em modo somente leitura.</div>}
        </div>
      ) : tab === "arquivos" ? (
        <div className="rounded-2xl border border-white/[0.07] bg-card p-3 space-y-2 min-h-[200px]" data-testid="files-tab">
          {meta.can_send && (
            <button data-testid="files-upload-btn" onClick={() => fileRef.current?.click()} disabled={uploading} className="w-full flex items-center justify-center gap-2 h-11 rounded-xl border border-dashed border-white/15 text-sm text-zinc-300 hover:border-accent/50 hover:text-accent">
              <input ref={fileRef} type="file" className="hidden" onChange={onFile} />
              {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Paperclip className="h-4 w-4" />}Enviar arquivo
            </button>
          )}
          {files.length === 0 ? <p className="text-center text-sm text-zinc-600 py-8">Nenhum arquivo compartilhado ainda.</p> : files.map((m) => (
            <a key={m.id} href={fileUrl(m.file.file_id)} target="_blank" rel="noreferrer" data-testid={`filecard-${m.id}`} className="flex items-center gap-3 rounded-xl border border-white/10 bg-black/20 p-2.5 hover:border-accent/40">
              {m.file.kind === "image" ? <img src={fileUrl(m.file.file_id)} alt="" className="h-11 w-11 rounded-lg object-cover shrink-0" /> : <span className="h-11 w-11 rounded-lg bg-[#1C1C24] flex items-center justify-center shrink-0"><FileTypeIcon kind={m.file.kind} ctype={m.file.file_type} /></span>}
              <span className="min-w-0 flex-1"><span className="block text-sm text-zinc-100 truncate">{m.file.file_name}</span><span className="block text-[11px] text-zinc-500">{kb(m.file.file_size || 0)} • {m.sender_name} • {dayOf(m.created_at)}</span></span>
              <Download className="h-4 w-4 text-zinc-400 shrink-0" />
            </a>
          ))}
        </div>
      ) : (
        <div className="rounded-2xl border border-white/[0.07] bg-card p-3 space-y-3 min-h-[200px]" data-testid="avisos-tab">
          {isOrg && meta.can_send && (
            <div className="rounded-xl border border-accent/30 bg-accent/[0.05] p-3">
              <label className="text-xs font-semibold text-accent flex items-center gap-1.5"><Megaphone className="h-4 w-4" />Publicar um aviso para a equipe</label>
              <textarea data-testid="announce-input" value={announceText} onChange={(e) => setAnnounceText(e.target.value)} rows={2} placeholder="Ex.: Lembrete: o check-in abre às 16h." className="w-full mt-2 bg-[#09090B] border border-white/10 rounded-xl p-2.5 text-sm text-zinc-100 resize-none focus:outline-none focus:border-accent/50" />
              <div className="flex justify-end mt-2"><Button data-testid="announce-btn" onClick={postAnnounce} disabled={!announceText.trim()} size="sm" className="rounded-xl bg-[#FF4D00] hover:bg-[#E64500] gap-1"><Megaphone className="h-4 w-4" />Publicar aviso</Button></div>
            </div>
          )}
          {announcements.length === 0 ? <p className="text-center text-sm text-zinc-600 py-8">Nenhum aviso publicado ainda.</p> : announcements.map((m) => (
            <div key={m.id} className="rounded-xl border border-accent/40 bg-accent/[0.06] p-3" data-testid={`aviso-${m.id}`}>
              <div className="flex items-center gap-2 flex-wrap"><Crown className="h-4 w-4 text-accent" /><span className="font-head font-bold text-accent text-sm">{m.sender_name || "Organizador"}</span><span className="text-[10px] text-zinc-500 ml-auto">{dayOf(m.created_at)} • {timeOf(m.created_at)}</span></div>
              <p className="text-sm text-zinc-100 mt-1.5 whitespace-pre-wrap break-words">{m.content}</p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function Messages() {
  const { eventId } = useParams();
  return eventId ? <Conversation eventId={eventId} /> : <ChatList />;
}
